import Joi from "joi";
import { useEffect, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { connect, useDispatch } from "react-redux";
import {
  getColleges,
  getUserTypes,
  getUserPageBlocks,
} from "../../../../../action/common/commonActionCreator";
import { updateApp } from "../../../../../action/masters/appActionCreator";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import MultipleSelect from "../../../../../common-components/multipleSelect";
import {
  baseURL,
  cloneDeep,
  maxImageSize,
  urlRegex,
} from "../../../../../constants/commonConstants";
import { validateFields } from "../../../../../helper/joi-validation";
import {
  getAlertData,
  alertConstants,
} from "../../../../../helper/sweet-alert";
import { IEditApp, IAppData } from "../../../../../models/master/apps";
import { setApp } from "../../../../../store/slices/master/appSlice";
import { UserTypes } from "../../../../../models/dashboard/adminDashboard";
import LoaderSpinner from "../../../../../common-components/loader";
import { masterType } from "../../../../../constants/masterTypes";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { setPageBlocks } from "../../../../../store/slices/common/commonSlice";
import { areObjectsEqual } from "../../../../../helper/generic-helpers";

const AppEdit = (props: IEditApp) => {
  const dispatch = useDispatch();
  const {
    updateApp,
    app,
    colleges,
    getColleges,
    getUserTypes,
    pageBlocks,
    userTypes,
    setShowEdit,
    getAllApps,
    getUserPageBlocks,
  } = props;

  const [data, setData] = useState<IAppData>(cloneDeep(app || {}));
  const [initialData, setInitialData] = useState<IAppData | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasChange, setHasChange] = useState(false);

  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    const _data = { ...data, [event.target.name]: event.target.value };
    if (event.target.name === "userType") {
      getUserPageBlocks(Number(event.target.value), masterType.app);
      _data.pageBlockIds = [];
      if (Number(event.target.value) === UserTypes.Staff) {
        _data.collegeIds = colleges?.map((college) => college.id);
      } else {
        _data.collegeIds = [];
      }
    }
    setData(_data);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    errors && delete errors[event.target.name];
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setImageName(selectedFile.name);
      const fileType = selectedFile.type;
      const fileSize = selectedFile.size;
      const validFileTypes = [
        "image/png",
        "image/ico",
        "image/svg+xml",
        "image/x-icon",
      ];
      if (!validFileTypes.includes(fileType)) {
        setErrors({
          ...errors,
          file: "Invalid file type. Please upload an icon.",
        });
      } else if (fileSize > maxImageSize) {
        setErrors({
          ...errors,
          file: "File size exceeds 1MB. Please upload a small file.",
        });
        setImagePreview(null);
        setImageName(null);
      } else {
        setData({ ...data, [event.target.name]: selectedFile });
        const fileURL = URL.createObjectURL(selectedFile);
        setImagePreview(fileURL);
      }
    }
  };

  useEffect(() => {
    if (app) {
      let _app = cloneDeep(app);
      setData(_app);
      setInitialData(_app);
      getUserPageBlocks(Number(_app.userType), masterType.app);
      if (_app.image) {
        const imageUrl = baseURL + _app.image;
        setImageName(_app.image);
        setImagePreview(imageUrl);
      }
    }
  }, [app]);

  const setAlert = (type: string) => {
    let alertData = getAlertData(type);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getAllApps();
      setShowEdit(false);
    }
  };

  const schema = Joi.object().keys({
    name: Joi.string().required().label("Name is required"),
    userType: Joi.number().greater(0).required().label("Select User Type"),
    file: Joi.any().when("isFile", {
      is: true,
      then: Joi.any().optional(),
      otherwise: Joi.any().required().label("File is required"),
    }),
    description: Joi.string().required().label("Description is required"),
    url: Joi.string().required().regex(urlRegex).label("Enter Valid URL"),
    pageBlockIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select Page Block"),
    collegeIds: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select University Management"),
  });

  const validationData = {
    ...data,
    isFile: Boolean(data.file),
  };

  const onUpdate = async () => {
    const validationErrors = validateFields(validationData, schema);
    if (validationErrors === undefined) {
      const formData = new FormData();
      formData.append("id", data.id?.toString() || "");
      formData.append("userType", data.userType?.toString() || "");
      formData.append("name", data.name);
      formData.append("file", data.file);
      formData.append("description", data.description.replace(/\s+$/g, ""));
      formData.append("url", data.url);
      formData.append("isActive", "true");
      data.pageBlockIds?.map((item: number, index: number) => {
        formData.append(`pageBlockIds[${index}]`, item.toString());
      });
      data.collegeIds?.map((item: number, index: number) => {
        formData.append(`collegeIds[${index}]`, item.toString());
      });

      setLoading(true);
      setIsDisabled(true);
      try {
        await updateApp(formData, data.id ?? 0, setAlert);
      } finally {
        setLoading(false);
        setIsDisabled(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  useEffect(() => {
    if (data && initialData) {
      setHasChange(!areObjectsEqual(data, initialData));
    }
  }, [data, initialData]);

  useEffect(() => {
    getColleges();
    getUserTypes();
    return () => {
      dispatch(setApp(undefined));
      dispatch(setPageBlocks([]));
    };
  }, []);

  return (
    <>
      {data && (
        <>
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
              Update App
            </h1>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => setShowEdit(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-md-12 form-group">
                <FloatingLabelDropDown
                  label={"User Type "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.userType}
                  name={"userType"}
                  optionsList={sortAlphabetically(
                    userTypes?.filter((type) => type.id !== UserTypes.Freshman)
                  )}
                />
                {errors?.userType && (
                  <div className="text-danger">{errors.userType}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"Name"}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.name}
                  name={"name"}
                  maxLength={50}
                />
                {errors?.name && (
                  <div className="text-danger">{errors.name}</div>
                )}
              </div>
              <div className="col-md-6 form-group">
                <div className="d-flex">
                  <label className="profile_details_text">
                    Icon<i className="label-asterisk"> * </i>
                  </label>
                  <p className="ms-2 label-info">
                    <i className="fa-solid fa-circle-info"></i>
                    <span>
                      Upload an icon (SVG, PNG, or ICO). For best results, use a
                      square shape with a transparent background.
                    </span>
                  </p>
                </div>
                <div className="inputfile-flex">
                  {imagePreview && (
                    <div className="inputfile-img">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ width: "50px", height: "50px" }}
                      />
                    </div>
                  )}
                  <div className="inputfile-wrap">
                    <input
                      type="file"
                      name="file"
                      id="image"
                      className="form-control d-none"
                      required
                      onChange={onFileChange}
                      accept="image/png, image/svg+xml, image/x-icon, image/ico, .svg, .png, .ico,"
                    />
                    <div className="inputfile">
                      <label htmlFor="image">Choose File</label>
                      <span>
                        {imageName
                          ? imageName.trim().split("/").pop()
                          : "No File Choosen"}
                      </span>
                    </div>
                  </div>
                </div>
                {errors?.file && (
                  <div className="text-danger">{errors.file}</div>
                )}
              </div>
              <div className="col-md-12 form-group">
                <label className="profile_details_text">
                  Description <i className="label-asterisk"> * </i>
                </label>
                <textarea
                  name="description"
                  value={data.description}
                  onChange={onChange}
                  maxLength={1000}
                ></textarea>
                {errors?.description && (
                  <div className="text-danger">{errors.description}</div>
                )}
              </div>

              <div className="col-md-6 form-group">
                <FloatingLabelTextInput
                  label={"URL "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.url}
                  name={"url"}
                  maxLength={500}
                />
                {errors?.url && <div className="text-danger">{errors.url}</div>}
              </div>
              <div className="col-md-6 form-group">
                <MultipleSelect
                  label={"Page-Block "}
                  onChange={onChange}
                  isMandatory={true}
                  values={data.pageBlockIds as number[]}
                  name={"pageBlockIds"}
                  optionList={sortAlphabetically(pageBlocks)}
                />
                {errors?.pageBlockIds && (
                  <div className="text-danger">{errors.pageBlockIds}</div>
                )}
              </div>
              <div className="col-md-12 form-group">
                <MultipleSelect
                  label={"University Management"}
                  onChange={onChange}
                  isMandatory={true}
                  values={data.collegeIds as number[]}
                  name={"collegeIds"}
                  optionList={sortAlphabetically(colleges)}
                />
                {errors?.collegeIds && (
                  <div className="text-danger">{errors.collegeIds}</div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onUpdate}
                disabled={isDisabled || !hasChange}
              >
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => setShowEdit(false)}
                disabled={isDisabled}
              >
                Close
              </button>
            </div>
          </div>

          <SweetAlert
            show={showAlert}
            success={false}
            type={alertType}
            title={alertTitle}
            onConfirm={() => onConfirm()}
            showCancel={showAlertCancel}
            onCancel={() => {
              setShowAlert(false);
              setShowAlertCancel(false);
            }}
            cancelBtnText={"No"}
            confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
          ></SweetAlert>
          {loading && <LoaderSpinner />}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = {
  updateApp: (data: any, id: number, callBack: Function) =>
    updateApp(data, id, callBack),

  getColleges: () => getColleges(),
  getUserTypes: () => getUserTypes(),
  getUserPageBlocks: (userType: number, masterType: string) =>
    getUserPageBlocks(userType, masterType),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    pageBlocks: state.common.pageBlocks,
    userTypes: state.common.userTypes,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AppEdit);
